import type { AfgorelseDetailModel } from "@/interfaces/afgorelse";
import { PaabudStatus } from "@/interfaces/paabud";

export type AfgorelseDetailVariant =
  "pending" | "submitted" | "afsluttet" | "bero" | "lukket";

/**
 * Afgør hvilken visning en afgørelse skal have.
 *
 * Rækkefølgen er ikke tilfældig. En åben kladde slår alt: kan advokaten indberette, er
 * det dét, siden skal handle om. Dernæst en indsendt indberetning. Først derefter deler
 * de to passive tilstande sig, og de betyder vidt forskellige ting:
 *
 * - `bero` — ingen frist. Indfrielsesperioden løber kun, mens advokaten er udøvende, så
 *   en deponeret beskikkelse sætter forløbet på pause. Der er intet at gøre, og der går
 *   ingen tid tabt.
 * - `afsluttet` — afgørelsen er lukket uden en indsendt indberetning. Det sker, når den
 *   henlægges, fordi advokaten er død. Der er intet mere at gøre.
 * - `lukket` — fristen er fastsat, men flowet er lukket. AIKA har flyttet indberetningen
 *   videre fra "frist ordinær", typisk fordi sekretariatet er ved at behandle den.
 *
 * At skelne på `frist == null` er sikkert: FristBeregning regner frem i tiden, så en
 * advokat, der er udøvende med en åben periode, får altid en beregnet frist — også når
 * den ligger langt ude i fremtiden. Mangler fristen, er forløbet i bero.
 */
export const resolveAfgorelseDetailVariant = (
  detail: Pick<
    AfgorelseDetailModel,
    "harAabenIndberetning" | "indsendt" | "frist" | "status"
  >,
): AfgorelseDetailVariant => {
  if (detail.harAabenIndberetning) {
    return "pending";
  }

  if (detail.indsendt != null) {
    return "submitted";
  }

  // EFTER submitted, aldrig før: en OPFYLDT afgørelse er også Lukket, og den skal blive ved
  // med at vise sin kvittering. Kommer vi hertil, er afgørelsen lukket uden indsendelse.
  if (detail.status === PaabudStatus.Lukket) {
    return "afsluttet";
  }

  if (detail.frist == null) {
    return "bero";
  }

  return "lukket";
};
